import { Search, X } from 'lucide-react';
import * as React from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// Shared free-text search box. Use this for every "type to filter a list"
// input so the leading magnifying-glass icon and the inline clear (X) affordance
// stay consistent app-wide. Entity pickers ("pick one from a list") should use
// `Combobox` instead — it owns its own clear/caret affordances.
//
// The value is fully controlled: pass `value` plus `onValueChange`, which
// receives the new string directly (no synthetic event to unwrap). The inline
// `X` shows only when there's text to clear and, when clicked or via Escape,
// empties the box and returns focus to the input so the user can keep typing.
//
// Layout note: the wrapper defaults to `w-80` so every list/filter search box
// is the same width app-wide. `className` styles the wrapper (this is the flex
// child, so width overrides go here — a later `w-64`/`max-w-sm`/… wins via
// tailwind-merge). Use `inputClassName` for tweaks that must land on the input
// element itself (e.g. `h-8`, `text-sm`).
export interface SearchInputProps
  extends Omit<React.ComponentProps<'input'>, 'value' | 'onChange' | 'type' | 'className'> {
  value: string;
  onValueChange: (value: string) => void;
  /** Styles the wrapper (and thus the flex child) — width goes here. */
  className?: string;
  /** Styles the inner input element — height/typography tweaks go here. */
  inputClassName?: string;
}

export function SearchInput({
  value,
  onValueChange,
  className,
  inputClassName,
  placeholder = 'Search…',
  disabled,
  ...props
}: SearchInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const showClear = value.length > 0 && !disabled;

  const clear = () => {
    onValueChange('');
    inputRef.current?.focus();
  };

  return (
    <div className={cn('relative w-80', className)}>
      <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && value) {
            e.preventDefault();
            clear();
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        // pl-8 clears the leading icon; pr-8 clears the trailing X when shown.
        className={cn('w-full pl-8', showClear && 'pr-8', inputClassName)}
        {...props}
      />
      {showClear ? (
        <button
          type="button"
          aria-label="Clear search"
          tabIndex={-1}
          // Keep focus on the input so the trailing blur doesn't fight the clear.
          onMouseDown={(e) => e.preventDefault()}
          onClick={clear}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground/60 transition-colors hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}
